import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import KensukeSymbol from '../components/common/KensukeSymbol';
import PageMeta from '../components/common/PageMeta';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { buildAdminMailtoHref, COMMUNICATION_BOARD_UI_ENABLED } from '../utils/config';
import { homeCanonicalSegment } from '../utils/publicAccessPaths';
import KensukeFeatureGrid from '../components/home/KensukeFeatureGrid';
import KensukeAiContentSection from '../components/home/KensukeAiContentSection';
import KensukeProjectOverview from '../components/home/KensukeProjectOverview';
import KensukeSeoFaq from '../components/home/KensukeSeoFaq';
import KensukeCitationBlock from '../components/home/KensukeCitationBlock';
import KensukeStructuredData from '../components/home/KensukeStructuredData';
import surface from '../components/home/KensukeSurface.module.scss';

function AuthButtons({ loggedIn }: { loggedIn: boolean }): ReactNode {
  const { t } = useTranslation();
  if (loggedIn) {
    return (
      <Button as={Link as any} to="/dashboard" variant="primary" className="ms-lg-2" size="sm">
        {t('dashboard.title', { defaultValue: 'ダッシュボード' })}
      </Button>
    );
  }
  return (
    <>
      <Button as={Link as any} to="/login" variant="primary" className="ms-lg-2" size="sm">
        {t('home.login', { defaultValue: 'ログイン' })}
      </Button>
      <Button as={Link as any} to="/register" variant="outline-primary" size="sm">
        {t('home.register', { defaultValue: '新規登録' })}
      </Button>
    </>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const loggedIn = Boolean(user);
  const isAdmin = isAdminUser(user);
  const canonicalPath = homeCanonicalSegment(location.pathname);

  return (
    <>
      <PageMeta
        titleKey="home.title"
        descriptionKey="home.meta_description"
        keywordsKey="home.meta_keywords"
        path={canonicalPath}
        homeAlternates
      />
      <KensukeStructuredData />

      <Navbar bg="white" expand="lg" className="navbar-light border-bottom">
        <Container>
          <Navbar.Brand as={Link as any} to="/" className={`fw-bold text-dark ${surface.navBrand}`}>
            <KensukeSymbol size={28} />
            <span>{t('home.brand')}</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link href="#ai-contents" className="text-dark fw-semibold">
                {t('home.nav_ai_contents', { defaultValue: 'AIコンテンツ' })}
              </Nav.Link>
              <Nav.Link href="#features" className="text-dark fw-semibold">
                {t('home.nav_features')}
              </Nav.Link>
              <Nav.Link href="#about" className="text-dark fw-semibold">
                {t('home.nav_about')}
              </Nav.Link>
              <Nav.Link href="#faq" className="text-dark fw-semibold">
                {t('home.nav_faq', { defaultValue: 'FAQ' })}
              </Nav.Link>
              <Nav.Link href="#cite" className="text-dark fw-semibold">
                {t('home.nav_cite', { defaultValue: '引用' })}
              </Nav.Link>
              {COMMUNICATION_BOARD_UI_ENABLED ? (
                <Nav.Link as={Link as any} to="/contact" className="text-dark fw-semibold">
                  {t('home.nav_contact_private', { defaultValue: 'お問い合わせ（個別）' })}
                </Nav.Link>
              ) : (
                <Nav.Link href={buildAdminMailtoHref()} className="text-dark fw-semibold">
                  {t('home.nav_contact', { defaultValue: 'お問い合わせ' })}
                </Nav.Link>
              )}
            </Nav>
            <Nav className="align-items-lg-center gap-lg-1">
              <LanguageSwitcher />
              <AuthButtons loggedIn={loggedIn} />
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main>
        <section className={surface.hero} aria-labelledby="home-hero-heading">
          <Container>
            <div className={surface.heroInner}>
              <div className={surface.heroAccent} aria-hidden />
              <div>
                <p className={surface.heroTagline}>
                  {t('home.hero_tagline', { defaultValue: '個人開発プロジェクト' })}
                </p>
                <h1 id="home-hero-heading" className={surface.heroTitle}>
                  {t('home.hero_title', { defaultValue: 'kensuke-system' })}
                </h1>
                <p className={surface.heroLead}>
                  {t('home.hero_subtitle', {
                    defaultValue: '自分用と友人依頼で作った Web ツールを公開しています。',
                  })}
                </p>
              </div>
            </div>
          </Container>
        </section>

        <KensukeAiContentSection surface="home" isLoggedIn={loggedIn} isAdmin={isAdmin} />

        <section id="features" className={surface.section} aria-labelledby="home-features-heading">
          <Container>
            <h2 id="home-features-heading" className={surface.sectionHeading}>
              {t('home.features_title', { defaultValue: 'ツール一覧' })}
            </h2>
            <p className={surface.sectionLead}>
              {t('home.features_lead', {
                defaultValue:
                  'ログイン不要で試せるツールから、研究向けの管理ツールまで。気になるものから開いてください。',
              })}
            </p>
            <KensukeFeatureGrid
              surface="home"
              isLoggedIn={loggedIn}
              isAdmin={isAdmin}
              group="tool"
            />
          </Container>
        </section>

        <KensukeProjectOverview />
        <KensukeSeoFaq />
        <KensukeCitationBlock />

        <section className={surface.footerStrip}>
          <Container className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 text-muted small">
              <KensukeSymbol size={28} variant="mono" />
              <span>
                {t('home.developer_info_detail', { defaultValue: '開発：new_kuu21' }).split('\n')[0]}
                <span className="mx-2" aria-hidden="true">
                  ·
                </span>
                {t('home.footer_multilang_note', {
                  defaultValue: '日本語・English など 8言語対応（右上から切り替え）',
                })}
              </span>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
}
